import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum, SubCategorias, SubcategoriasPagingRes, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchSubCategorias = ({
  page = 1,
  page_size = 10,
  nombre = '',
}: GetSubCategoriasParams) => {
  return useQuery({
    queryKey: ['sub-categorias', page, nombre, page_size],
    queryFn: async () => {
      return getSubCategorias({ page, nombre, page_size });
    },
  });
};

export const useGetSubCategoria = (id: string) => {
  return useQuery({
    queryKey: ['sub-categorias', id],
    queryFn: async () => {
      return getSubCategoria(id);
    },
  });
};

export const useCreateSubCategorias = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createSubCategorias,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub-categorias'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Subcategoria creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateSubCategorias = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateSubCategorias,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub-categorias'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Subcategoria actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetSubCategoriasParams = Partial<SubCategorias> & {
  page?: number;
  page_size?: number;
  nombre?: string;
};

export type CreateSubCategoriasParams = Omit<SubCategorias, 'id_subcategoriaproducto'>;
export type UpdateSubCategoriasParams = {
  id: string;
  data: CreateSubCategoriasParams;
};

export const getSubCategorias = async (params?: GetSubCategoriasParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<SubcategoriasPagingRes>(
    `${urlBackend}api/v1/subcategoria-producto/?${queryParams}`,
    true
  );
};

export const getSubCategoria = async (id: string) => {
  try {
    return await get<SubCategorias>(`${urlBackend}api/v1/subcategoria-producto/${id}/`, true);
  }
  catch (error) {
    return handleAxiosError(error);
  }
};

export const createSubCategorias = async (data: CreateSubCategoriasParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<SubCategorias>(`${urlBackend}api/v1/subcategoria-producto/`, data, true);
};


export const updateSubCategorias = async ({ id, data }: UpdateSubCategoriasParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return put<SubCategorias>(`${urlBackend}api/v1/subcategoria-producto/${id}/`, data, true);
};
